import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ContentPage } from '@/components/marketing/content-page';

const FAQS = [
  {
    q: 'What is knowledge base software?',
    a: 'Knowledge base software lets your team publish self-service articles — how-tos, troubleshooting guides, FAQs — on a public helpdesk your customers can search and read on their own. 81% of customers try self-service before contacting support; a good knowledge base answers those questions before they become tickets.',
  },
  {
    q: 'How much does knowledge base software cost?',
    a: 'Prices depend on the model. Zendesk includes its knowledge base in Suite plans, which start at $55/agent/month (checked 2026-10-19). HubSpot includes one from Service Hub Starter, with Professional listed from $90/seat/month (checked 2026-10-21). Kora charges 1 credit per published article (about $0.10) — writing, editing, branding and analytics are all free.',
  },
  {
    q: 'How does Kora’s knowledge base pricing work?',
    a: 'Publishing an article costs 1 credit, charged once per article — the same creation-only principle as tickets. Editing is unlimited and free. Unpublishing and republishing are free. Search, votes, pageviews, subscriber emails and branding never cost anything. Images and uploaded videos count toward your normal storage usage (2 credits per 2GB per month).',
  },
  {
    q: 'Can I customize and brand my helpdesk?',
    a: 'Yes. Your helpdesk lives at usekora.app/p/helpdesk/yourbrand (claim a custom link — you get up to 5) and supports your logo, title, intro, accent color, separate light/dark text and background colors, and independent title/body fonts from a curated list. The default look is clean monochrome with a light/dark/system theme toggle.',
  },
  {
    q: 'Does my helpdesk get its own analytics?',
    a: 'Yes — visitors, pageviews, referrers, top articles, helpful-vote ratios and on-site searches including zero-result queries (your content-gap queue), plus captured subscriber emails you can export as CSV.',
  },
  {
    q: 'Is my helpdesk optimized for search engines?',
    a: 'Each article has its own slug, meta title, meta description and cover image, plus a per-article switch to include it in the sitemap. Private or unlisted content stays out of search automatically. You can also keep the entire helpdesk reachable by direct link only.',
  },
  {
    q: 'What editor does Kora use for articles?',
    a: 'A Notion-style editor: type "/" for a block menu (headings, lists, quotes, code, tables, dividers, images, videos), markdown shortcuts like "#" and "-", and a floating toolbar for formatting. Images upload inline; videos can be YouTube/Vimeo/Loom embeds or direct MP4/WebM uploads.',
  },
  {
    q: 'Can I share article drafts before publishing?',
    a: 'Yes. Every article has a draft share link (valid 7 days, revocable anytime) so teammates or clients can review content before you spend the publish credit.',
  },
  {
    q: 'Can readers subscribe to my helpdesk?',
    a: 'Yes — an optional email subscribe box on your helpdesk home collects addresses (with bot protection), which you manage and export from the dashboard. One click to CSV.',
  },
  {
    q: 'Does the knowledge base work with Kora’s AI features?',
    a: 'That’s the design center: your published articles are one of the three sources Kora AI answers from, next to your uploaded files and the exception rules you write. The articles you publish now are what Kora AI cites in drafts and answers.',
  },
];

const COMPARISON = [
  { scenario: 'Knowledge base included', kora: 'Yes, no plan needed', zendesk: 'In Suite plans, from $55/agent/mo (checked 2026-10-19)', hubspot: 'From Service Hub Starter (checked 2026-10-21)' },
  { scenario: 'Article limits', kora: 'No limit stated; 1 credit per published article', zendesk: 'Not compared', hubspot: 'Starter: 1 KB, up to 2,000 articles; Professional: 100 KBs, up to 10,000' },
  { scenario: 'Cost to publish', kora: '1 credit (once), edits free', zendesk: 'Part of the per-agent price', hubspot: 'Part of the per-seat price' },
  { scenario: 'Memorable custom URL', kora: 'Claim up to 5 links', zendesk: 'Not compared', hubspot: 'Not compared' },
  { scenario: 'Helpdesk analytics', kora: 'Visitors, pageviews, referrers, searches', zendesk: 'Not compared', hubspot: 'Not compared' },
];

export function KnowledgeBaseSoftwarePage() {
  useEffect(() => {
    const title = 'Knowledge Base Software — 1 Credit per Article | Kora';
    const description = 'Publish a branded help center with analytics, SEO controls and email subscriptions. Notion-style editor and theming. 1 credit per published article.';
    setDocumentMeta(title, description);
    setCanonical('/knowledge-base-software');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/knowledge-base-software', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'Knowledge Base', url: 'https://usekora.app/knowledge-base-software' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);

    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Feature guide"
      title="Knowledge base software that ships with your helpdesk"
      subtitle="A public, branded, blog-style helpdesk — with its own analytics — for 1 credit per published article. Everything else is free."
      answer={
        <>
          <strong>Kora's Helpdesk is a blog, not a folder tree.</strong> Notion-style editor, claimable custom
          links, full theming, per-article SEO controls, subscriptions and visitor analytics. Per-seat tools fold this into seat
          pricing. Kora charges 1 credit per article — edits forever free.
        </>
      }
      ctaTitle="Publish your first article"
      ctaSubtitle="1 credit. Your branded helpdesk is live in minutes."
    >
      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mt-4 mb-3">
        Why a knowledge base pays for itself
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        81% of customers attempt self-service before they ever contact you. Every question your helpdesk answers
        is a ticket nobody has to file — and on a pay-as-you-go helpdesk, that's money directly back in your wallet.
        Industry deflection runs 30–60% once a real article corpus exists.
      </p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        It compounds further: the articles you publish become the source your AI assistant draws from when it drafts
        replies and resolves conversations. The knowledge base isn't a side feature — it's the fuel tank.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        What you get
      </h2>
      <div className="space-y-3 mb-6">
        {[
          { title: 'A branded home at your own link', body: 'usekora.app/p/helpdesk/yourbrand — claim up to 5 custom links, keep every old link working, default to a clean monochrome look with light/dark/system theming.' },
          { title: 'A Notion-style editor', body: 'Type "/" for blocks — headings, lists, quotes, code, tables, images, videos (embeds or uploads). Markdown shortcuts, floating toolbar, autosave, revisions with restore.' },
          { title: 'Per-article control', body: 'Slug, summary, cover image, meta title and description, public/unlisted visibility, and a per-article search-engine switch that feeds the sitemap.' },
          { title: 'Built-in analytics', body: 'Visitors, pageviews, referrers, top articles, helpful votes, and on-site searches — zero-result searches become your content backlog.' },
          { title: 'Email subscriptions', body: 'An optional subscribe box captures reader emails with bot protection. Export to CSV anytime.' },
          { title: 'Help widget mode', body: 'Embed your whole helpdesk in your site with one script tag: KoraWidget with mode "help".' },
        ].map((f) => (
          <div key={f.title} className="rounded-xl border border-zinc-200 p-3.5">
            <p className="text-xs font-semibold text-zinc-900 ">{f.title}</p>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        Pricing that makes per-seat vendors uncomfortable
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        Publishing costs 1 credit — once per article, ever. A 30-article helpdesk costs about $3 total.
        Editing, republishing, theming, analytics, subscriptions and search are always free. Attachments meter
        into the same storage billing as everything else (2 credits per 2GB/month).
      </p>
      <div className="overflow-x-auto rounded-2xl border border-zinc-200 my-6">
        <table className="w-full min-w-[640px] text-sm bg-white ">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 ">
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Scenario</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Kora</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Zendesk</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">HubSpot</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 ">
            {COMPARISON.map((row) => (
              <tr key={row.scenario}>
                <td className="px-4 py-3 font-medium text-zinc-900 ">{row.scenario}</td>
                <td className="px-4 py-3 text-emerald-600 ">{row.kora}</td>
                <td className="px-4 py-3 text-zinc-600 ">{row.zendesk}</td>
                <td className="px-4 py-3 text-zinc-600 ">{row.hubspot}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        Publishing model
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 mb-6 list-disc pl-5">
        <li><strong>1 credit per article, charged once:</strong> the first time you publish. The button always shows the cost before you click.</li>
        <li><strong>Unlimited free edits:</strong> fix typos, rewrite sections, swap covers — maintenance should never be taxed.</li>
        <li><strong>Unpublish and republish free:</strong> taking an article down or putting it back never charges again.</li>
        <li><strong>Unlisted mode:</strong> live at a direct link, invisible to listings, search and sitemaps — like an unlisted video.</li>
        <li><strong>Draft share links:</strong> 7-day signed links for review before you spend the credit.</li>
      </ul>

      <p className="text-sm text-zinc-600 mb-6">
        Want customers to follow their own requests too? See <Link to="/customer-portal-software" className="underline text-zinc-900">customer portal software</Link>.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-4">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-zinc-100 border-y border-zinc-100 mb-8">
        {FAQS.map((faq) => (
          <details key={faq.q} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 ">
              {faq.q}
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
        <p className="text-sm text-zinc-700 leading-relaxed">
          Ready to publish? <Link to="/auth" className="font-medium text-blue-600 hover:underline">Create a free workspace</Link>,
          open Helpdesk, and start from a template. Or see{' '}
          <Link to="/pricing" className="font-medium text-blue-600 hover:underline">exactly how credits work</Link> —{' '}
          a 30-article helpdesk costs 30 credits, total.
        </p>
      </div>
    </ContentPage>
  );
}
